/**
 * useOperatorProfile — operator self-cabinet «Анкета» backbone.
 *
 * Wraps the operator-self contract under /api/operator/*:
 *   GET  /api/operator/profile        — profile + change status + news + media
 *   PUT  /api/operator/profile        — save (applied | pending depending on autoApprove)
 *   POST /api/operator/media          — upload photo/video (multipart/form-data)
 *   PUT  /api/operator/media/:id/crops  — named normalised crop regions
 *   PUT  /api/operator/media/:id/filter — filter preset/spec
 *   POST /api/operator/news / PUT /api/operator/news/:id — draft / submit a post
 *
 * Mirrors the useOoDay shape ({ loading, error, reload }) with an AbortController
 * guard, plus typed mutators. Media/crop/filter mutations refresh the local
 * snapshot optimistically so the live preview updates without a full reload.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { saveDraft, loadDraft, clearDraft } from "../lib/profileDraft";

/** Статус автосохранения анкеты для UI: «Сохранено / Сохраняю / Нет связи». */
export type SaveStatus = "saved" | "saving" | "offline";

/* ── Contract types (mirror /api/operator/profile) ─────────────────────────── */

/** Named crop usages — each maps to a place the asset is shown on the client. */
export type CropUsage = "avatar" | "dossier_hero" | "media_tile" | "report_avatar" | "news_cover";

/** Normalised crop region (0..1 of the original). */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type MediaKind = "photo" | "video";

export interface MediaAsset {
  id: number;
  kind: MediaKind;
  label: string;
  fileKey: string;
  thumbKey: string;
  width: number;
  height: number;
  fanOnly: boolean;
  fanPortrait: boolean;
  filter: Record<string, unknown>;
  crops: Partial<Record<CropUsage, CropRect>>;
  streamUrl: string;
  posterUrl: string;
  createdAt: number;
}

export interface ActorProfileFields {
  photo_url: string;
  character_name: string;
  display_role: string;
  character_status: string;
  atmos_status: string;
  temple_title: string;
  temple_blurb: string;
  /** JSON-string of a string[] — archive bullet list. */
  archive_notes: string;
  trailer_src: string;
  special_opens_at: string;
  special_text: string;
  special_quest_id: string;
  is_public: boolean;
}

export type ChangeStatus = "pending" | "approved" | "rejected" | "needs_revision";

export interface ProfileChange {
  id: number;
  status: ChangeStatus;
  reviewNote: string | null;
  submittedAt: number | null;
  reviewedAt: number | null;
}

export type NewsStatus = "draft" | "pending" | "published" | "rejected";

export interface NewsPost {
  id: number;
  title: string;
  body: string;
  coverMediaId: number | null;
  status: NewsStatus;
  publishedAt: number | null;
}

export interface ProfileResponse {
  staffId: string;
  nickname: string;
  profile: ActorProfileFields;
  autoApprove: boolean;
  isDraft: boolean;
  change: ProfileChange | null;
  news: NewsPost[];
  media: MediaAsset[];
}

export interface SaveResult {
  ok: boolean;
  status?: "applied" | "pending";
  changeId?: number;
  autoApprove?: boolean;
  error?: string;
}

export interface UploadResult {
  ok: boolean;
  mediaId?: number;
  kind?: MediaKind;
  error?: string;
}

export interface NewsResult {
  ok: boolean;
  id?: number;
  status?: NewsStatus;
  error?: string;
}

/* News write body (title/body/cover/submit are all optional per contract). */
export interface NewsInput {
  title?: string;
  body?: string;
  coverMediaId?: number | null;
  submit?: boolean;
}

export interface OperatorProfileResult {
  data: ProfileResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /**
   * Save profile fields → applied (autoApprove) or pending.
   * opts.auto=true — фоновое автосохранение: тихо (бэк не пингует OO) и БЕЗ reload,
   * чтобы перезагрузка снапшота не затирала то, что оператор печатает прямо сейчас.
   * opts.auto=false/нет — явная кнопка: с уведомлением OO и обновлением снапшота.
   */
  saveProfile: (profile: ActorProfileFields, opts?: { auto?: boolean }) => Promise<SaveResult>;
  /** Upload one photo/video. Refreshes media list on success. */
  uploadMedia: (file: File, opts?: { label?: string; fanOnly?: boolean; width?: number; height?: number }) => Promise<UploadResult>;
  /** Persist named crop regions for one asset (optimistic local update). */
  saveCrops: (mediaId: number, crops: Partial<Record<CropUsage, CropRect>>) => Promise<{ ok: boolean; error?: string }>;
  /** Persist filter spec for one asset (optimistic local update). */
  saveFilter: (mediaId: number, filter: Record<string, unknown>) => Promise<{ ok: boolean; error?: string }>;
  /** Toggle fan-only on one asset (optimistic local update; persisted via filter). */
  setFanOnly: (mediaId: number, fanOnly: boolean) => Promise<{ ok: boolean; error?: string }>;
  /** Mark one asset as THE fan-portrait (exclusive; клиент показывает его подписчикам). */
  setFanPortrait: (mediaId: number, fanPortrait: boolean) => Promise<{ ok: boolean; error?: string }>;
  /** Create (id omitted) or update (id given) a news post. Refreshes on success. */
  saveNews: (input: NewsInput, id?: number) => Promise<NewsResult>;
  /** Permanently delete one media asset (owner-scoped). Refreshes on success. */
  deleteMedia: (mediaId: number) => Promise<{ ok: boolean; error?: string }>;
  /** Статус автосейва для UI-чипа («Сохранено / Сохраняю / Нет связи»). */
  saveStatus: SaveStatus;
  /** Несохранённый черновик с прошлой сессии (сбой связи) для восстановления; null если нет. */
  draftFields: ActorProfileFields | null;
  /** Отбросить восстановленный черновик и убрать его из хранилища. */
  dismissDraft: () => void;
}

/** Empty profile so the inline form always has a controlled shape pre-fetch. */
export const EMPTY_PROFILE: ActorProfileFields = {
  photo_url: "",
  character_name: "",
  display_role: "",
  character_status: "",
  atmos_status: "",
  temple_title: "",
  temple_blurb: "",
  archive_notes: "[]",
  trailer_src: "",
  special_opens_at: "",
  special_text: "",
  special_quest_id: "",
  is_public: false,
};

export function useOperatorProfile(): OperatorProfileResult {
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [draftFields, setDraftFields] = useState<ActorProfileFields | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const staffIdRef = useRef<string>("");                        // ключ черновика
  const lastAutoRef = useRef<ActorProfileFields | null>(null);  // последняя попытка автосейва — для ретрая
  const reload = useCallback(() => setTrigger((n) => n + 1), []);
  const dismissDraft = useCallback(() => { setDraftFields(null); clearDraft(staffIdRef.current); }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);

    api<ProfileResponse>("/api/operator/profile", { signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        const norm = normalize(res);
        setData(norm);
        setLoading(false);
        // Офлайн-устойчивость: остался несохранённый черновик и он отличается от серверного
        // профиля → предложить восстановить (draftFields). Иначе чистим.
        const sid = (res.staffId as string) || "";
        staffIdRef.current = sid;
        const d = loadDraft<ActorProfileFields>(sid);
        setDraftFields(d && JSON.stringify(d.fields) !== JSON.stringify(norm.profile) ? d.fields : null);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить анкету");
        setLoading(false);
      });

    return () => ac.abort();
  }, [trigger]);

  const saveProfile = useCallback(async (profile: ActorProfileFields, opts?: { auto?: boolean }): Promise<SaveResult> => {
    const auto = opts?.auto === true;
    if (auto) lastAutoRef.current = profile; // для авто-ретрая при возврате связи
    setSaveStatus("saving");
    try {
      const res = await api<SaveResult>("/api/operator/profile", { method: "PUT", body: { profile, auto } });
      if (res && res.ok === false) {
        // Сервер ответил, но отверг — данные не теряем, держим черновик.
        setSaveStatus("offline");
        saveDraft(staffIdRef.current, profile);
        return { ok: false, error: res.error ?? "Не удалось сохранить" };
      }
      setSaveStatus("saved");
      clearDraft(staffIdRef.current);
      setDraftFields(null);
      if (auto) {
        // Без полного reload — только синхронизируем снапшот, чтобы не сбить активный ввод.
        setData((prev) => (prev ? { ...prev, profile: { ...prev.profile, ...profile } } : prev));
      } else {
        reload();
      }
      return { ...res, ok: true };
    } catch (e: unknown) {
      // Сбой связи — НЕ теряем правки: черновик в localStorage, статус offline, дошлём при возврате связи.
      setSaveStatus("offline");
      saveDraft(staffIdRef.current, profile);
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка сохранения" };
    }
  }, [reload]);

  // Авто-ретрай автосейва при возврате связи / возврате во вкладку.
  useEffect(() => {
    const retry = () => {
      if (saveStatus !== "offline") return;
      const f = lastAutoRef.current;
      if (f && (typeof navigator === "undefined" || navigator.onLine !== false)) void saveProfile(f, { auto: true });
    };
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", retry);
    return () => { window.removeEventListener("online", retry); document.removeEventListener("visibilitychange", retry); };
  }, [saveStatus, saveProfile]);

  const uploadMedia = useCallback(async (
    file: File,
    opts?: { label?: string; fanOnly?: boolean; width?: number; height?: number },
  ): Promise<UploadResult> => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts?.label) fd.append("label", opts.label);
    if (opts?.fanOnly) fd.append("fanOnly", "1");
    if (opts?.width) fd.append("width", String(opts.width));
    if (opts?.height) fd.append("height", String(opts.height));
    try {
      const res = await api<UploadResult>("/api/operator/media", { method: "POST", body: fd, retries: 0 });
      if (res && res.ok === false) return { ok: false, error: res.error ?? "Загрузка не удалась" };
      reload();
      return { ...res, ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Загрузка не удалась" };
    }
  }, [reload]);

  /** Patch a single asset in the local snapshot without a network round-trip. */
  const patchAsset = useCallback((mediaId: number, patch: Partial<MediaAsset>) => {
    setData((prev) => prev
      ? { ...prev, media: prev.media.map((m) => (m.id === mediaId ? { ...m, ...patch } : m)) }
      : prev);
  }, []);

  const saveCrops = useCallback(async (
    mediaId: number,
    crops: Partial<Record<CropUsage, CropRect>>,
  ): Promise<{ ok: boolean; error?: string }> => {
    patchAsset(mediaId, { crops });
    try {
      const res = await api<{ ok: boolean }>(`/api/operator/media/${mediaId}/crops`, { method: "PUT", body: { crops } });
      if (res && res.ok === false) return { ok: false, error: "Не удалось сохранить кадрирование" };
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка кадрирования" };
    }
  }, [patchAsset]);

  const saveFilter = useCallback(async (
    mediaId: number,
    filter: Record<string, unknown>,
  ): Promise<{ ok: boolean; error?: string }> => {
    patchAsset(mediaId, { filter });
    try {
      const res = await api<{ ok: boolean }>(`/api/operator/media/${mediaId}/filter`, { method: "PUT", body: { filter } });
      if (res && res.ok === false) return { ok: false, error: "Не удалось сохранить фильтр" };
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка фильтра" };
    }
  }, [patchAsset]);

  /**
   * fan-only — настоящий столбец media_assets.fan_only (его и читает клиент при
   * гейтинге медиа). Пишем через выделенный роут; в filter-спеку флаг НЕ кладём.
   */
  const setFanOnly = useCallback(async (
    mediaId: number,
    fanOnly: boolean,
  ): Promise<{ ok: boolean; error?: string }> => {
    patchAsset(mediaId, { fanOnly });
    try {
      const res = await api<{ ok: boolean }>(`/api/operator/media/${mediaId}/fan-only`, { method: "PUT", body: { fanOnly } });
      if (res && res.ok === false) return { ok: false, error: "Не удалось сменить доступ" };
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка доступа" };
    }
  }, [patchAsset]);

  // Фан-портрет — эксклюзивный флаг (один на актёра): оптимистично снимаем у всех, ставим у одного.
  const setFanPortrait = useCallback(async (
    mediaId: number,
    fanPortrait: boolean,
  ): Promise<{ ok: boolean; error?: string }> => {
    setData((prev) => prev
      ? { ...prev, media: prev.media.map((m) => ({ ...m, fanPortrait: fanPortrait && m.id === mediaId })) }
      : prev);
    try {
      const res = await api<{ ok: boolean }>(`/api/operator/media/${mediaId}/fan-portrait`, { method: "PUT", body: { fanPortrait } });
      if (res && res.ok === false) return { ok: false, error: "Не удалось задать фан-портрет" };
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка фан-портрета" };
    }
  }, []);

  const saveNews = useCallback(async (input: NewsInput, id?: number): Promise<NewsResult> => {
    try {
      const path = id ? `/api/operator/news/${id}` : "/api/operator/news";
      const res = await api<NewsResult>(path, { method: id ? "PUT" : "POST", body: input });
      if (res && res.ok === false) return { ok: false, error: res.error ?? "Не удалось сохранить новость" };
      reload();
      return { ...res, ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Ошибка новости" };
    }
  }, [reload]);

  const deleteMedia = useCallback(async (mediaId: number): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await api<{ ok: boolean; error?: string }>(`/api/operator/media/${mediaId}`, { method: "DELETE" });
      if (res && res.ok === false) return { ok: false, error: res.error ?? "Не удалось удалить" };
      reload();
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : "Не удалось удалить" };
    }
  }, [reload]);

  return { data, loading, error, reload, saveProfile, uploadMedia, saveCrops, saveFilter, setFanOnly, setFanPortrait, saveNews, deleteMedia, saveStatus, draftFields, dismissDraft };
}

/** Defensive normalisation: the backend may omit nested objects on a fresh row. */
function normalize(res: ProfileResponse): ProfileResponse {
  const profile = { ...EMPTY_PROFILE, ...(res.profile ?? {}) };
  const media = (res.media ?? []).map((m) => ({
    ...m,
    filter: m.filter ?? {},
    crops: m.crops ?? {},
    // fan-only — столбец media_assets.fan_only (его же гейтит клиент). Единый source of truth.
    fanOnly: !!m.fanOnly,
    fanPortrait: !!m.fanPortrait,
  }));
  return {
    ...res,
    profile,
    media,
    news: res.news ?? [],
    autoApprove: !!res.autoApprove,
    isDraft: !!res.isDraft,
    change: res.change ?? null,
  };
}

/** Parse the archive_notes JSON-string field into a safe string[]. */
export function parseArchiveNotes(json: string): string[] {
  try {
    const arr = JSON.parse(json || "[]");
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
